import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import type { CredentialType } from '@truecare/schemas';
import { Alert, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';
import { ArtifactRow, type ArtifactRowData } from './ArtifactRow';
import { ArtifactUploadModal } from './ArtifactUploadModal';

/**
 * Phase 5f-b — caregiver credentials section.
 *
 * Fetches the catalog from /me/caregiver/credentials/catalog
 * (role-conditional per 5f-2) and renders one `<ArtifactRow>` per
 * required type. Click handler opens `<ArtifactUploadModal>` which
 * captures the file + dates; the section runs the full upload flow:
 *
 *   1. POST /me/caregiver/credentials/upload-url
 *   2. PUT file directly to S3 (using the signed URL)
 *   3. POST /me/caregiver/credentials with the resulting key + extras
 *   4. Invalidate the catalog + application-readiness queries
 *
 * The row's `extras.externalReferenceNumber` (when present per the
 * catalog's extraFields metadata) is captured into the upload's
 * record-request payload.
 *
 * Per 5f-1 lock: section mounts for APPLYING + ACTIVE statuses; the
 * parent /me page gates visibility. Backend independently enforces
 * the same status gate at the upload-url + record endpoints (Phase
 * 5 design — pre-active caregivers complete vetting; ACTIVE caregivers
 * re-upload renewals).
 */

/** Per-type helper copy shown in the upload modal. Keyed by
 *  CredentialType; types without a hint fall through to null. */
const CREDENTIAL_UPLOAD_HINTS: Partial<Record<CredentialType, string>> = {
  GOVERNMENT_ID: "Accepted: driver's licence or passport.",
  FOOD_HANDLER:
    'Your Ontario Food Handler certification card or certificate, plus the licence number.',
};

interface PendingUpload {
  // Narrowed to the credential enum so the upload-url + record POSTs
  // typecheck without `as any` (audit r1 finding).
  type: CredentialType;
  label: string;
  extras: Record<string, string>;
  promptDates: boolean;
}

export function CaregiverCredentialsSection({
  readOnly = false,
}: { readOnly?: boolean } = {}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<PendingUpload | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const catalogQuery = useQuery({
    queryKey: ['caregiver', 'credentials', 'catalog'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/credentials/catalog')),
  });

  const uploadMutation = useMutation({
    mutationFn: async (input: {
      type: CredentialType;
      extras: Record<string, string>;
      file: File;
      issuedAt: string | null;
      expiresAt: string | null;
    }) => {
      // Step 1: signed URL
      const urlResp = await unwrap(
        api.POST('/api/v1/me/caregiver/credentials/upload-url', {
          body: {
            type: input.type,
            contentType: input.file.type,
            contentLength: input.file.size,
          },
        }),
      );
      // Step 2: PUT file to S3
      const putResp = await fetch(urlResp.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': input.file.type },
        body: input.file,
      });
      if (!putResp.ok) {
        throw new Error(
          `Could not upload to storage (HTTP ${String(putResp.status)}). Try again.`,
        );
      }
      // Step 3: record credential
      const externalRef = input.extras['externalReferenceNumber'];
      const recordResp = await unwrap(
        api.POST('/api/v1/me/caregiver/credentials', {
          body: {
            type: input.type,
            objectStorageKey: urlResp.objectStorageKey,
            externalReferenceNumber:
              externalRef === undefined || externalRef === '' ? null : externalRef,
            issuedAt: input.issuedAt,
            expiresAt: input.expiresAt,
          },
        }),
      );
      return recordResp;
    },
    onSuccess: () => {
      // Catalog + readiness both depend on the credential set.
      qc.invalidateQueries({ queryKey: ['caregiver', 'credentials', 'catalog'] });
      qc.invalidateQueries({ queryKey: ['caregiver', 'application-readiness'] });
      setPending(null);
      setUploadError(null);
    },
    onError: (e) => {
      setUploadError(messageOf(e, 'Upload failed. Try again.'));
    },
  });

  if (catalogQuery.isPending) {
    return (
      <Card padding="lg" accent="left" tone="forest">
        <p className="font-body text-sm text-ink-muted">Loading your credentials…</p>
      </Card>
    );
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <Card padding="lg" accent="left" tone="forest">
        <Alert tone="danger" title="Couldn't load your credentials">
          Please refresh the page.
        </Alert>
      </Card>
    );
  }

  function handleRowAction(item: ArtifactRowData) {
    return ({ extras }: { type: string; extras: Record<string, string> }) => {
      setUploadError(null);
      setPending({
        // Catalog only emits canonical CredentialType members; safe cast.
        type: item.type as CredentialType,
        label: item.label,
        extras,
        promptDates: true,
      });
    };
  }

  return (
    <>
      <Card padding="lg" accent="left" tone="forest" data-testid="caregiver-credentials-section">
        <CardHeader
          title="Credentials"
          description="Upload your professional certifications + screening documents. Operators verify each item before activating your account."
        />
        <div className="mt-4">
          {catalogQuery.data.items.length === 0 && (
            <p className="font-body text-sm text-ink-muted">
              No credential uploads required for your selected roles.
            </p>
          )}
          {catalogQuery.data.items.map((item: ArtifactRowData) => (
            <ArtifactRow
              key={item.type}
              data={item}
              onUpload={handleRowAction(item)}
              readOnly={readOnly}
            />
          ))}
        </div>
      </Card>

      <ArtifactUploadModal
        open={pending !== null}
        title={pending ? `Upload ${pending.label}` : ''}
        promptDates={pending?.promptDates ?? true}
        referenceDisplay={pending?.extras['externalReferenceNumber'] ?? null}
        hint={pending ? CREDENTIAL_UPLOAD_HINTS[pending.type] ?? null : null}
        error={uploadError}
        onCancel={() => {
          setPending(null);
          setUploadError(null);
        }}
        onSubmit={async ({ file, issuedAt, expiresAt }) => {
          if (pending === null) return;
          await uploadMutation.mutateAsync({
            type: pending.type,
            extras: pending.extras,
            file,
            issuedAt,
            expiresAt,
          });
        }}
      />
    </>
  );
}
